import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'

import { setRedirect } from '../../../appActions'
import { RootState } from '../../../appReducer'
import { DirectScheduling } from '../../../models/enums'
import { useAppDispatch } from '../../../util/useAppDispatch'
import { fetchPractice } from '../../practices/actions'
import AvailabilityPracticeHeader from '../../shared/AvailabilityPracticeHeader'

import AvailabilityFilters from './AvailabilityFilters'
import AvailabilityWeeklyView from './AvailabilityWeeklyView'

import './Availability.sass'

export type AvailabilityRouteParams = RouteComponentProps<{
    practiceId: string
}>

type Props = AvailabilityRouteParams

const moduleName = 'availability'

const Availability = (props: Props) => {
    const practiceId = props.match.params.practiceId

    const [loading, setLoading] = useState(false)
    const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
        from: moment().format('YYYY-MM-DD'),
        to: moment()
            .add(6, 'days')
            .format('YYYY-MM-DD'),
    })

    const practice = useSelector((state: RootState) => state.practices.practices[practiceId])
    const selectedLocationId = useSelector((state: RootState) => state.v2.availability.selectedLocationId[practiceId])
    const availabilities = useSelector((state: RootState) => state.v2.availability.availabilities?.[selectedLocationId])

    const practiceFetched = practice?.id

    const dispatch = useAppDispatch()

    useEffect(() => {
        if (!practiceFetched) {
            dispatch(fetchPractice(practiceId))
        }
    }, [practiceFetched, practiceId, dispatch])

    const onReturnToPractices = () => {
        dispatch(setRedirect(`/practices`))
    }

    const isLocationSuspended = (location: Models.PracticeLocation) =>
        location.isVyneControlled && Array.isArray(location.vyneProducts) && location.vyneProducts.length === 0

    const selectedLocation = practice?.locations?.find(l => l.id === selectedLocationId)
    const isDirectSchedulingActive = selectedLocation
        ? !isLocationSuspended(selectedLocation) &&
          selectedLocation.products?.some(p => p.product.value === DirectScheduling.value && p.active)
        : practice?.locations?.length
          ? practice.locations.some(
                location =>
                    !isLocationSuspended(location) &&
                    location.products?.some(p => p.product.value === DirectScheduling.value && p.active),
            )
          : practice?.products?.some(p => p.value === DirectScheduling.value && p.active)

    if (!practice) {
        return <div>Loading...</div>
    }

    return (
        <div className={moduleName}>
            <div className="detached-button" onClick={onReturnToPractices}>
                <i className="material-icons">arrow_back</i>
            </div>
            <AvailabilityPracticeHeader practice={practice} />
            {!isDirectSchedulingActive ? (
                <div className={`${moduleName}__suspended`}>
                    <div className={`${moduleName}__title`}>Availability Unavailable</div>
                    <div className={`${moduleName}__subtitle-wrapper`}>
                        <div className={`${moduleName}__subtitle`}>
                            Online scheduling is not active for this practice.
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className={`${moduleName}__title`}>Availability</div>
                    <div className={`${moduleName}__subtitle-wrapper`}>
                        <div className={`${moduleName}__subtitle`}>View the corresponding schedule.</div>
                    </div>
                    <AvailabilityFilters practiceId={practiceId} setLoading={setLoading} dateRange={dateRange} />
                    <AvailabilityWeeklyView data={availabilities} isPending={loading} setDateRange={setDateRange} />
                </>
            )}
        </div>
    )
}

export default Availability
